import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const feedbackItems = [
    {
        id: 1,
        text: "Partha was wonderful to work with. He was beyond patient as we worked through outlining a complex project - figuring out what was really needed togehter. He was thorough and helped by offering efficiencies and flow solutions we hadn't thought of ourselves yet. When putting everything into action and testing, he provide timely troubleshooting, solutions and fixes. I would absolutely use Partha again strongly recommend him.",
        author: "Jennifer Egan",
        company: "URLIFE Media",
        image: "/Clients/JenniferEgan.jpeg",
    },
    {
        id: 2,
        text: "Great experience with Partha. He knew how to figure out a number of complicated requests, and he enhanced every part of our project with upgraded formatting and quick turnaround. He provided an experience even better than I imagined when I started out, so will definitely use him for all projects going forward.",
        author: "Grant Parker",
        company: "Multiple Businesses",
        image: "/Clients/GrantParker.png",
    },
    {
        id: 3,
        text: "Really knowledgeable guy. Is patient and also keeps pushing until he knows for sure that he understands. This is a nr 1 skill that a lot of others don't have and is a really big thing in data related tasks. Will work with him again.",
        author: "Koen Bouwens",
        company: "Airtable <-> Webflow Automation",
        image: "/Clients/Koen Bouwens.png",
    },
    {
        id: 4,
        text: "It doesn't get better than Partha. He's an absolute professional with immense talent and great problem-solving skills. Thank you so much for the opportunity to work with you.",
        author: "Luke Palder",
        company: "Laika Ventures, LLC",
        image: "/Clients/Luke Palder.jpeg",
    },
];

export const FeedbackCarousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    useEffect(() => {
        if (!isAutoPlaying) return;
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % feedbackItems.length);
        }, 6000);
        return () => clearInterval(interval);
    }, [isAutoPlaying]);

    const goToPrevious = () => {
        setIsAutoPlaying(false);
        setCurrentIndex((prev) => (prev - 1 + feedbackItems.length) % feedbackItems.length);
    };

    const goToNext = () => {
        setIsAutoPlaying(false);
        setCurrentIndex((prev) => (prev + 1) % feedbackItems.length);
    };

    return (
        <section id="feedback" className="py-20 md:py-28 bg-[#FAFAFA]" data-testid="feedback-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <p className="text-[#34A853] text-sm font-medium uppercase tracking-widest mb-4">
                        Client Feedback
                    </p>
                    <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C2C2C]">
                        What Clients Say
                    </h2>
                </motion.div>

                {/* Carousel */}
                <div className="relative max-w-4xl mx-auto">
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.4 }}
                        className="bg-white p-8 md:p-12 rounded-2xl shadow-card border border-[#E5E5E5]"
                        data-testid={`feedback-card-${currentIndex}`}
                    >
                        <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                            {/* Image & Name Column */}
                            <div className="flex-shrink-0 flex flex-col items-center gap-4 text-center md:w-48">
                                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-[#34A853]/10 shadow-sm">
                                    <img 
                                        src={feedbackItems[currentIndex].image} 
                                        alt={feedbackItems[currentIndex].author}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[#2C2C2C] font-bold text-base md:text-lg">{feedbackItems[currentIndex].author}</p>
                                    <p className="text-[#6B6B6B] text-xs md:text-sm font-medium">{feedbackItems[currentIndex].company}</p>
                                </div>
                            </div>

                            {/* Text Column */}
                            <div className="flex-grow text-center md:text-left border-t md:border-t-0 md:border-l border-slate-100 pt-6 md:pt-0 md:pl-8 mt-2 md:mt-0">
                                <Quote size={32} className="text-[#34A853]/20 mb-4 mx-auto md:mx-0" />
                                <p className="text-[#475569] text-lg leading-relaxed italic">
                                    "{feedbackItems[currentIndex].text}"
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Navigation */}
                    <div className="flex items-center justify-center gap-4 mt-8">
                        <button
                            onClick={goToPrevious}
                            className="w-10 h-10 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center text-[#6B6B6B] hover:text-[#34A853] hover:border-[#34A853] transition-colors duration-200"
                            data-testid="feedback-prev-btn"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        
                        <div className="flex items-center gap-2">
                            {feedbackItems.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setIsAutoPlaying(false);
                                        setCurrentIndex(index);
                                    }}
                                    className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                                        index === currentIndex ? 'bg-[#34A853]' : 'bg-[#E5E5E5]'
                                    }`}
                                    data-testid={`feedback-dot-${index}`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={goToNext}
                            className="w-10 h-10 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center text-[#6B6B6B] hover:text-[#34A853] hover:border-[#34A853] transition-colors duration-200"
                            data-testid="feedback-next-btn"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};
